export type PurchaseOrderStatus =
  | "CREATED"
  | "SENT"
  | "PARTIALLY_RECEIVED"
  | "RECEIVED"
  | "CANCELLED";

export type PurchaseOrderDelivery = {
  id: string;
  deliveryNo: string;
  quantity: number;
  expectedQuantity: number;
  shortageQuantity: number;
  returnQuantity: number;
  returnReason: string | null;
  returnDisposition: "REPLACE" | "DEDUCT_FROM_ORDER" | null;
  deliveredAt: string;
  receiptNote: string;
  deliveryNoteNo: string | null;
  documents: PurchaseOrderDocument[];
  qualityStatus: "PENDING" | "ACCEPTED" | "CONDITIONAL" | "REJECTED";
  qualityNote: string | null;
  rejectionDisposition: "REPLACE" | "DEDUCT_FROM_ORDER" | "CONDITIONAL_ACCEPTANCE" | "CREATE_CAPA" | null;
  capaNo: string | null;
  capaStatus: "UNDER_REVIEW" | "ACTION_IN_PROGRESS" | "EFFECTIVENESS_REVIEW" | "CLOSED" | null;
  delayDays: number;
  recordedBy: string;
  createdAt: string;
};

export type PurchaseOrderDocument = {
  id: string;
  documentType: "DELIVERY_NOTE" | "INVOICE" | "QUALITY_REPORT" | "OTHER";
  originalName: string;
  contentType: string;
  sizeBytes: number;
  securityStatus: "PENDING" | "VALIDATED" | "CLEAN" | "BLOCKED" | "FAILED";
  createdAt: string;
};

export type SupplierPerformance = {
  score: number;
  risk: "LOW" | "MEDIUM" | "HIGH";
  orderCount: number;
  deliveryCount: number;
  onTimeRate: number | null;
  qualityAcceptanceRate: number | null;
  shortageRate: number;
  returnRate: number;
  cancellationRate: number;
  historicalSuccessRate: number | null;
  priceConsistencyRate: number | null;
  rationale: string[];
};

export type PurchaseOrder = {
  id: string;
  orderNo: string;
  requestNo: string;
  requestTitle: string;
  requesterName: string;
  supplierName: string;
  selectedOfferNo: string;
  status: PurchaseOrderStatus;
  currency: string;
  totalAmount: number;
  paymentDays: number;
  deliveryDays: number;
  orderedQuantity: number;
  receivedQuantity: number;
  deductedQuantity: number;
  replacementQuantity: number;
  unit: string;
  expectedDeliveryAt: string;
  actualDeliveryAt: string | null;
  assignedSpecialistName: string;
  sourceSystem: "PLATFORM" | "MICRO_ERP";
  externalReference: string | null;
  overdue: boolean;
  version: number;
  createdAt: string;
  sentAt: string | null;
  cancellationReason: string | null;
  dispatchStatus: "NOT_SENT" | "PENDING" | "SENT" | "FAILED";
  dispatchChannel: "EMAIL" | null;
  dispatchRecipient: string | null;
  deliveryAddress: string | null;
  dispatchError: string | null;
  suggestedDispatchRecipient: string | null;
  confirmationStatus: "NOT_REQUESTED" | "AWAITING" | "ACCEPTED" | "REJECTED" | "CHANGE_REQUESTED";
  confirmationUpdatedAt: string | null;
  approvalSource: "GENERAL_MANAGER" | "CONTRACT_PREAUTHORIZED";
  offerVersion: number;
  contractNo: string | null;
  contractVersion: number | null;
  supplierResponses: Array<{
    id: string;
    responseType: "ACCEPTED" | "REJECTED" | "CHANGE_REQUESTED";
    reason: string | null;
    deliveryPlan: Array<{ date: string; quantity: number; note?: string }>;
    createdAt: string;
  }>;
  deliveries: PurchaseOrderDelivery[];
  events: PurchaseOrderEvent[];
  supplierPerformance: SupplierPerformance;
};

export type PurchaseOrderManagementOverview = {
  metrics: { automaticOrders:number; contractedSpend:number; blockedOperations:number; expiringContracts:number; shadowAssessments:number; shadowAwaitingDecision:number; shadowMatches:number; shadowFalsePositives:number };
  contracts: Array<{ contractNo:string;supplierName:string;validTo:string;totalLimit:string;usedLimit:string;remainingLimit:string }>;
  exceptions: Array<{ id:string;result:string;rfqNo:string;supplierName:string;createdAt:string }>;
  shadowReviews: Array<{ id:string;rfqNo:string;supplierName:string;predictedRoute:"WOULD_PREAUTHORIZE"|"NORMAL_APPROVAL";reason:string;actualAction:"APPROVED"|"REJECTED"|null;classification:"MATCH"|"FALSE_POSITIVE"|"HUMAN_REVIEW_REQUIRED"|null;assessedAt:string;comparedAt:string|null }>;
};

export type PurchaseOrderEvent = {
  id: string;
  eventType: string;
  previousStatus: string | null;
  newStatus: string;
  actorName: string;
  actorRole: string;
  reason: string | null;
  occurredAt: string;
};

export type PurchaseOrderCandidate = {
  requestNo: string;
  requestTitle: string;
  quantity: number;
  unit: string;
  neededAt: string;
  supplierName: string;
  offerNo: string;
  amount: number;
  currency: string;
  unitPrice: number;
  materialAmount: number;
  shippingCost: number;
  insuranceCost: number;
  installationCost: number;
  otherCost: number;
  paymentDays: number;
  deliveryDays: number;
  expectedDeliveryAt: string;
  decidedBy: string;
  decidedAt: string;
  requestVersion: number;
  offerVersion: number;
  evaluationVersion: number;
};

export const purchaseOrderStatusLabel: Record<PurchaseOrderStatus, string> = {
  CREATED: "Taslak",
  SENT: "Tedarikçiye iletildi",
  PARTIALLY_RECEIVED: "Kısmi teslimat",
  RECEIVED: "Teslim alındı",
  CANCELLED: "İptal edildi",
};

export function purchaseOrderOperationalLabel(order: PurchaseOrder) {
  const stage = purchaseOrderLifecycleStage(order);
  if (stage === "QUALITY_PENDING") return "Kalite kontrolü bekleniyor";
  if (stage === "QUALITY_REJECTED") {
    const capa = order.deliveries.find((delivery) => delivery.capaNo);
    return capa ? "DÖF sürecinde" : "Kalite uygun bulunmadı";
  }
  if (stage === "COMPLETED") return "Tamamlandı";
  return purchaseOrderStatusLabel[order.status];
}

export function remainingQuantity(order: PurchaseOrder) {
  return Math.max(0, order.orderedQuantity - order.deductedQuantity - order.receivedQuantity);
}

export function deliveryProgress(order: PurchaseOrder) {
  const effectiveQuantity = order.orderedQuantity - order.deductedQuantity;
  return effectiveQuantity <= 0
    ? 100
    : Math.min(100, Math.round((order.receivedQuantity / effectiveQuantity) * 100));
}

export type PurchaseOrderLifecycleStage =
  | "DRAFT"
  | "SENT"
  | "DELIVERY"
  | "QUALITY_PENDING"
  | "QUALITY_REJECTED"
  | "COMPLETED"
  | "CANCELLED";

export function purchaseOrderLifecycleStage(
  order: PurchaseOrder,
): PurchaseOrderLifecycleStage {
  if (order.status === "CANCELLED") return "CANCELLED";
  if (order.status === "CREATED") return "DRAFT";
  if (!order.deliveries.length) return "SENT";
  const hasPendingQuality = order.deliveries.some(
    (delivery) => delivery.qualityStatus === "PENDING",
  );
  if (hasPendingQuality) return "QUALITY_PENDING";
  const hasRejectedQuality = order.deliveries.some(
    (delivery) => delivery.qualityStatus === "REJECTED" && (
      !delivery.rejectionDisposition
      || (delivery.rejectionDisposition === "REPLACE" && remainingQuantity(order) > 0)
      || (delivery.rejectionDisposition === "CREATE_CAPA" && delivery.capaStatus !== "CLOSED")
    ),
  );
  if (hasRejectedQuality) return "QUALITY_REJECTED";
  if (remainingQuantity(order) === 0) return "COMPLETED";
  return "DELIVERY";
}
